import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { ContentPageOverviewParams } from 'application/repositories/contentPageOverviewRepository';
import { alphabeticallySortArray } from 'helpers/array/array';
import { convertTimestampToDate, getLatestTimestamp, getOldestTimestamp, listViewPrettyDate } from 'helpers/date/date';
import { generateMonthSelectboxes, generateYearSelectboxes } from 'helpers/form/selectbox/selectboxGenerators';
import { generateId } from 'helpers/id';
import logger from 'helpers/logger';
import { mapSubjects } from 'helpers/mappers/subjectMapper';
import { handleShyHeading, removeSpecialCharactersAndTrim, replacePartOfString } from 'helpers/string/string';
import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { FormfieldSelectOption } from 'ui/components/2-molecules/Form/FormfieldSelect';
import { PaginationProps } from 'ui/components/2-molecules/Navigation/Pagination';
import { SearchResultItemProps } from 'ui/components/2-molecules/SearchResultItem';
import { SearchFilterProps } from 'ui/components/3-organisms/SearchFilter';
import { SearchFilterSelect } from 'ui/components/3-organisms/SearchFilter/SearchFilter';
import { SearchResultView, SearchResultViewProps } from 'ui/components/3-organisms/SearchResultView';
import { getSortedContentPagesFromApi } from '../../../application/adapters/contentPageOverviewAdapter';
import { getFormattedDateRange } from 'helpers/date/date';
import { useRouter } from 'next/router';
import { pushSearchFormParamsToUrl } from 'helpers/pushSearchFormParamsToUrl';

export const ContentPageOverviewFeature: React.FC<Content.ContentPageOverview> = ({ content }) => {
	const PageContext = usePageContext();
	const SiteContext = useSiteContext();
	const router = useRouter();
	const { themeSettings } = PageContext;
	const {
		category: contentCategory,
		link,
		rootFolder,
		lead,
		allResultsLabel,
		resultsLabel,
		oldestYear,
		contentOverviewSubjects,
		showSubjectDropdown,
		fallbackText,
	} = content.properties ?? {};
	const { host, culture } = SiteContext;
	const filterBaseId = generateId();

	const mapSubjectDocType = (category: string): Content.ContentPageCategory => {
		const theCategory = category as Content.ContentPageCategory;
		if (
			theCategory === 'Annoncering' ||
			theCategory === 'Nyhed' ||
			theCategory === 'Publikation' ||
			theCategory === 'Udbud' ||
			theCategory === 'Event'
		) {
			return theCategory;
		} else {
			logger.warn(
				'ContentPageOverviewFeature mapAndSetSubjectDocType() - category was not recognized: ',
				category,
			);
			return null;
		}
	};

	const subjects = contentOverviewSubjects && showSubjectDropdown ? mapSubjects(contentOverviewSubjects) : [];
	const dictionary = useDictionaryContext();
	const [results, setResults] = useState<SearchResultItemProps[]>([]);
	const [initialResults, setInitialResults] = useState<SearchResultItemProps[] | null>(null);
	const [paginationResponse, setPaginationResponse] = useState<Models.TeaserPagination>(null);
	/** Use this to increment when ever pagination is interacted with */
	const [nextPage, setNextPage] = useState(0);

	/* Determine the start year for the comboboxes */
	const oldestYearInt = convertTimestampToDate(oldestYear).getFullYear() ?? 0;
	// Events will be sorted by descending date, so we can use the first event of the initial results to determine the earliest event year
	const earliestEventYear =
		contentCategory === 'Event' && initialResults?.length > 0 ? new Date(results[0].dateTime).getFullYear() : 0;
	const comboBoxesStartYear =
		earliestEventYear >= oldestYearInt && earliestEventYear > 0
			? earliestEventYear
			: oldestYearInt > 0
			? oldestYearInt
			: new Date().getFullYear();

	const [searchTerm, setSearchTerm] = useState('');
	const [searchIncrementer, setSearchIncrementer] = useState(0);
	const [searchYear, setSearchYear] = useState<string>('');
	const [searchMonth, setSearchMonth] = useState<string>('');
	const [searchSubjects, setSearchSubjects] = useState<string[]>([]);
	const [searchParams, setSearchParams] = useState<ContentPageOverviewParams>(null);
	/**toggleSearch is just a boolean to listen for in useEffect. When ever it changes will initiate a useEffect where
	 * searchParams are updated and there upon useSWR kicks in. */
	const [toggleSearch, setToggleSearch] = useState(false);
	const [inSearchMode, setInSearchMode] = useState(false);
	const formFieldRef = useRef<HTMLInputElement>(null);

	const overwriteSearchField = (newString: string) => {
		if (formFieldRef?.current) {
			formFieldRef.current.value = newString;
		}
	};

	const mapAndSetResults = (results: Models.TeaserArticle[]) => {
		const sortedResultsDesc = results.map<SearchResultItemProps>((item) => {
			let dateString: string;

			if (contentCategory === 'Event') {
				if (item.end) {
					dateString = getFormattedDateRange({
						startDate: new Date(item.date),
						endDate: new Date(item.end),
						culture,
					});
				} else {
					dateString = listViewPrettyDate(new Date(item.date), culture, true);
				}
			} else {
				dateString = listViewPrettyDate(new Date(item.date), culture);
			}

			return {
				dateString,
				link: { url: item.url, name: item.url },
				dateTime: item.date !== null ? new Date(Date.parse(item.date)) : new Date(),
				heading: handleShyHeading({ heading: item.header, shyHeading: item.shyHeader }),
				text: item.lead ?? '',
			};
		});

		setResults(sortedResultsDesc);
	};

	const basicClearBeforeSearch = () => {
		mapAndSetResults([]);
		setPaginationResponse(null);
		setNextPage(0);
	};

	const fullClear = () => {
		basicClearBeforeSearch();
		setSearchYear('');
		setSearchMonth('');
		setSearchSubjects([]);
		setSearchTerm('');
		pushSearchFormParamsToUrl({
			url: window.location.href.split('?')[0],
			searchTerm: undefined,
			searchYearValue: undefined,
			searchMonthValue: undefined,
			topics: undefined,
			page: undefined,
			router,
		});

		// Overwrite current value of the searchField
		overwriteSearchField('');
	};

	/****** API fetches ******************/
	const { data, isLoading } = useSWR(searchParams, getSortedContentPagesFromApi, {
		revalidateOnFocus: false,
	});

	/***** useEffects ***********/
	useEffect(() => {
		if (!initialResults) {
			setInitialResults(results);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [results]);

	// On mount we look for URL search params and set the search state accordingly
	useEffect(() => {
		const initialSearchParams = new URLSearchParams(window.location.search);
		const searchParamsTerm = initialSearchParams.get('search');
		const searchYearValue = initialSearchParams.get('year');
		const searchMonthValue = initialSearchParams.get('month');
		const topics = initialSearchParams.get('topics')?.split(',');

		// If no relevant search params are present, do nothing
		if (!searchParamsTerm && !searchYearValue && !searchMonthValue && !topics) return;

		// Set form states based on URL search params
		handleSearchStringChanged(searchParamsTerm ?? '');
		setSearchYear(searchYearValue ?? '');
		setSearchMonth(searchMonthValue ?? '');
		setSearchSubjects(topics ?? []);

		// Trigger search
		setToggleSearch(!toggleSearch);
		setInSearchMode(true);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const searchYearValue = searchYear;
		const searchMonthValue = searchMonth;
		let fromDate: string;
		const toDate = getLatestTimestamp(searchYearValue, searchMonthValue);

		if (contentCategory === 'Event' && !searchYearValue && !searchMonthValue) {
			// Limit to events that starts today or later if no year or month is selected
			fromDate = new Date().toISOString().split('T')[0] + 'T00:00:00.000Z';
		} else {
			fromDate = getOldestTimestamp(searchYearValue, searchMonthValue);
		}

		const theSearch: ContentPageOverviewParams = {
			rootKey: rootFolder?.key,
			categories: [{ name: mapSubjectDocType(contentCategory) }],
			subjects: subjects.filter((subject) => {
				return searchSubjects.find((searchSubject) => subject.id === Number(searchSubject));
			}),
			nextPage: nextPage,
			to: toDate,
			from: fromDate,
			direction: contentCategory === 'Event' ? 'ascending' : 'descending',
			searchTerm: searchTerm,
			incrementer: searchIncrementer,
			host: host,
		};
		setSearchIncrementer(searchIncrementer + 1);
		setSearchParams(theSearch);

		// Push URL search params to browser URL history to reflect the search
		pushSearchFormParamsToUrl({
			url: window.location.href,
			searchTerm,
			searchYearValue,
			searchMonthValue,
			page: nextPage > 0 ? (nextPage + 1).toString() : undefined, // Add 1 to page number to reflect the visible page number (1-indexed)
			topics: searchSubjects,
			router,
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nextPage, toggleSearch]);

	useEffect(() => {
		if (data?.searchResults) {
			mapAndSetResults(data.searchResults);
			setPaginationResponse(data.pagination);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	/*****  Eventhandlers  *******/
	const handleSearchStringChanged = (value: string) => {
		const formattedSearchTerm = removeSpecialCharactersAndTrim(value);
		setSearchTerm(formattedSearchTerm);
	};

	const onPageChange = (selectedItem: { selected: number }) => {
		setNextPage(selectedItem.selected);
	};

	const onSubmitClicked = () => {
		basicClearBeforeSearch();

		// Since we remove specialCharacters everytime we update searchTerm with new value
		// We also update the searchField so user can see what term has actually been used on submit.
		overwriteSearchField(searchTerm);
		setToggleSearch(!toggleSearch);
		setInSearchMode(true);
	};

	const onClearClicked = () => {
		fullClear();
		setToggleSearch(!toggleSearch);
		setInSearchMode(false);
	};

	const toOptions = (
		allText: string,
		checkBoxes: { value: string; labelText: string }[],
	): FormfieldSelectOption[] => [
		{ text: allText, value: '' },
		...checkBoxes.map((item) => ({ text: item.labelText, value: item.value })),
	];

	const generateFilterSelects = (): SearchFilterSelect[] => {
		// The first option has no value, so it works as "no filter"
		const allOption = dictionary.getValue('Search.Filter.All', null, 'Alle');
		const sortedSubjects = alphabeticallySortArray(subjects, 'subjectName');

		const toReturn: SearchFilterSelect[] = [
			{
				id: filterBaseId + '-year',
				name: 'year',
				labelText: dictionary.getValue('Search.Filter.YearLabel', null, 'Årstal'),
				options: toOptions(
					allOption,
					generateYearSelectboxes(comboBoxesStartYear, new Date().getFullYear(), filterBaseId + '-year'),
				),
				value: searchYear,
				onChange: setSearchYear,
			},
			{
				id: filterBaseId + '-month',
				name: 'month',
				labelText: dictionary.getValue('Search.Filter.MonthLabel', null, 'Måned'),
				options: toOptions(allOption, generateMonthSelectboxes(filterBaseId + '-month', culture)),
				value: searchMonth,
				onChange: setSearchMonth,
			},
		];

		if (sortedSubjects?.length > 0) {
			toReturn.push({
				id: filterBaseId + '-subject',
				name: 'subject',
				labelText: dictionary.getValue('Search.Filter.SubjectLabel', null, 'Emne'),
				options: toOptions(
					allOption,
					sortedSubjects.map((item) => ({ value: item.id.toString(), labelText: item.subjectName })),
				),
				value: searchSubjects[0] ?? '',
				onChange: (value) => setSearchSubjects(value ? [value] : []),
			});
		}

		return toReturn;
	};

	const filterProps: SearchFilterProps = {
		heading: dictionary.getValue('Search.Filter.Heading', null, 'Søgning og filtrering'),
		searchField: {
			id: filterBaseId + '-textInput',
			name: 'search-filter',
			label: dictionary.getValue('Search.Filter.SearchPlaceholder', null, 'Indtast søgeord'),
			defaultValue: searchTerm,
			placeholder: `${dictionary.getValue('Search.Filter.SearchPlaceholder', null, 'Indtast søgeord')}...`,
			handleSearchStringChanged,
		},
		submitBtn: {
			text: dictionary.getValue('Search.Filter.Submit', null, 'Søg'),
			onClick: onSubmitClicked,
		},
		formFieldSelects: generateFilterSelects(),
		clearSearchBtn: {
			text: dictionary.getValue('Search.Filter.Clear', null, 'Nulstil søgning'),
			onClick: onClearClicked,
		},
		forwardedFormfieldStringRef: formFieldRef,
	};

	const getCorrectResultListHeading = () => {
		return inSearchMode
			? resultsLabel && resultsLabel?.length > 0
				? resultsLabel
				: dictionary.getValue(
						'Search.ResultList.Heading.Result',
						null,
						'Vi fandt ${num} resultater, der matcher din søgning',
				  )
			: allResultsLabel && allResultsLabel?.length > 0
			? allResultsLabel
			: dictionary.getValue('Search.ResultList.Heading.All', null, 'Alle resultater');
	};

	const resultListProps = (): SearchResultViewProps['resultListProps'] => {
		return {
			heading: replacePartOfString(
				getCorrectResultListHeading(),
				'${num}',
				paginationResponse?.totalResults.toString() ?? '0',
			),
			link: link,
			results: results,
			isLoading: isLoading,
		};
	};

	const pagination: PaginationProps = {
		pageCount: paginationResponse?.totalPages ?? 0,
		initialPageIndex: 0,
		pageText: dictionary.getValue('Search.ResultList.PaginationAriaLabel', null, 'Side'),
		previousAriaLabel: dictionary.getValue('Search.ResultList.PaginationPreviousAriaLabel', null, 'Forrige side'),
		nextAriaLabel: dictionary.getValue('Search.ResultList.PaginationNextAriaLabel', null, 'Næste side'),
		jumpBackwardAriaLabel: dictionary.getValue(
			'Search.ResultList.PaginationJumpBackwardAriaLabel',
			null,
			'Spring flere sider tilbage',
		),
		jumpForwardAriaLabel: dictionary.getValue(
			'Search.ResultList.PaginationJumpForwardAriaLabel',
			null,
			'Spring flere sider frem',
		),
		navigationAriaLabel: dictionary.getValue(
			'Search.ResultList.PaginationNavigationAriaLabel',
			null,
			'Sidenavigation',
		),
		onPageChange: onPageChange,
		forcePageIndex: paginationResponse?.totalPages > 0 ? nextPage : -1,
	};

	return (
		<SearchResultView
			hero={{
				heading: handleShyHeading(content?.properties),
				lead: lead,
				theme: themeSettings?.heroAndBreadcrumbColorTheme,
			}}
			filterProps={filterProps}
			resultListProps={resultListProps()}
			pagination={pagination}
			fallbackText={isLoading ? null : fallbackText}
		/>
	);
};
